import { useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Check, ChevronLeft, Copy, User, X } from "lucide-react";
import { customFetch } from "@workspace/api-client-react";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { extractApiErrorMessage } from "@/lib/api-error";
import { formatWhatsApp } from "@/lib/utils";
import { displayPhone } from "@/lib/whatsapp-bot";

// Interruptor «Robô» pelo POST /api/admin/robo/agente (estado/pausar/retomar). O estado é lido ao vivo; sem ele (503
// ROBO_ESTADO_INDISPONIVEL), o interruptor não aparece. Desligar a constante esconde o controle sem bater na rota.
const INTERRUPTOR_PRONTO = true;

/**
 * «Ver Agente» (/results, só o suporte): as conversas do robô do WhatsApp com os clientes de uma loja, do jeito que o
 * cliente viu. Pedido do Pedro em 14/10/2026, no molde do VetZap (robo-ia/ESPEC-ver-agente.md e as armadilhas da 14 da
 * referência). Os dados vêm de store_whatsapp_bot_messages pelas rotas /api/admin/robo/* (role dev).
 */

type Quem = "cliente" | "loja" | "robo" | "sistema";

export interface ConversaDoRobo {
  phone: string;
  chatid?: string | null;
  nome: string | null;
  ultima: string | null;
  ultimaQuem: Quem | null;
  // A última é a saudação automática do WhatsApp Business (sai pelo número da loja, mas não é uma pessoa).
  ultimaAutomatica?: boolean;
  ultimaTipo: string | null;
  ultimaEm: string | null;
  total: number;
  pausa: { motivo: string; ate?: string | null } | null;
}

export interface MensagemDoRobo {
  id: number | string;
  messageId: string | null;
  quem: Quem;
  automatica: boolean;
  // De onde veio (status, saudacao, ausencia, celular, whatsapp_business, ia, cliente, sistema); null = linha antiga.
  origem?: string | null;
  tipo: string | null;
  texto: string | null;
  transcricao: string | null;
  em: string;
  execucao: string | null;
}

// Quem mandou, pela origem gravada (Pedro, 20/10/2026: «gravar a conversa toda»). Sem origem, vale a regra de antes.
const ROTULO_DA_ORIGEM: Record<string, string> = {
  status: "Aviso de status do pedido",
  saudacao: "Saudação do robô",
  ausencia: "Ausência do robô",
  whatsapp_business: "Automática do WhatsApp Business",
  celular: "Loja (pelo celular)",
};

interface RespostaConversas {
  loja?: string | { nome?: string | null; name?: string | null } | null;
  conversas: ConversaDoRobo[];
}

interface EstadoDoAgente {
  pausado: boolean;
  motivo: string | null;
}

/** `/results?conversa=<storeId>:<phone>`: lê, tira da URL (o F5 não reabre) e divide no PRIMEIRO «:». Link ruim → null. */
export function lerConversaDaUrl(): { storeId: string; phone: string | null } | null {
  try {
    const url = new URL(window.location.href);
    const v = url.searchParams.get("conversa");
    if (v === null) return null;
    url.searchParams.delete("conversa");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    const i = v.indexOf(":");
    const storeId = (i < 0 ? v : v.slice(0, i)).trim();
    const phone = (i < 0 ? "" : v.slice(i + 1)).trim().replace(/@.*$/, "").replace(/\D/g, "");
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(storeId)) return null;
    return { storeId, phone: phone || null };
  } catch {
    return null;
  }
}

// "Copiado" só quando copiou de verdade (armadilha 5): Clipboard API e, de reserva, o textarea — conferindo o retorno.
export async function copiar(texto: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(texto);
      return true;
    }
  } catch {
    // cai na reserva
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = texto;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

const doisDigitos = (n: number) => String(n).padStart(2, "0");
// Hora no fuso do NAVEGADOR: é tela do suporte, e a loja é outra (armadilha 12).
export const hora = (iso: string) => {
  const d = new Date(iso);
  return `${doisDigitos(d.getHours())}:${doisDigitos(d.getMinutes())}`;
};
export const dia = (iso: string) => {
  const d = new Date(iso);
  return `${doisDigitos(d.getDate())}/${doisDigitos(d.getMonth() + 1)}/${d.getFullYear()}`;
};
export const horaDaLista = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  const hoje = new Date();
  return d.toDateString() === hoje.toDateString() ? hora(iso) : `${doisDigitos(d.getDate())}/${doisDigitos(d.getMonth() + 1)}`;
};

// Iniciais só de nome de verdade; sem nome, ícone — nunca iniciais de telefone (armadilha 4).
function iniciais(nome: string | null): string | null {
  const partes = (nome ?? "").split(/\s+/).filter((p) => /\p{L}/u.test(p));
  if (partes.length === 0) return null;
  const letra = (p: string) => (p.match(/\p{L}/u)?.[0] ?? "").toUpperCase();
  return partes.length === 1 ? letra(partes[0]!) : `${letra(partes[0]!)}${letra(partes[partes.length - 1]!)}`;
}

// O `phone` é a chave da conversa (sem o 9 do celular); o `chatid` tem o número inteiro.
const telefoneDe = (c: { phone: string; chatid?: string | null }) =>
  c.chatid ? formatWhatsApp(c.chatid.split("@")[0]!) : displayPhone(c.phone);

const rotuloDaMidia = (tipo: string | null): string | null =>
  tipo === "audio" ? "Áudio" : tipo === "image" ? "Foto" : tipo === "video" ? "Vídeo" : tipo === "document" ? "Documento" : tipo === "sticker" ? "Figurinha" : null;

export function Avatar({ nome }: { nome: string | null }) {
  const ini = iniciais(nome);
  return (
    <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold shrink-0">
      {ini ?? <User className="w-4 h-4" aria-hidden />}
    </div>
  );
}

function BotaoCopiar({ texto, rotulo, className = "" }: { texto: string; rotulo: string; className?: string }) {
  const { toast } = useToast();
  const [copiou, setCopiou] = useState(false);
  return (
    <button
      type="button"
      aria-label={rotulo}
      title={rotulo}
      className={`inline-flex items-center justify-center rounded p-0.5 text-muted-foreground hover:text-foreground ${className}`}
      onClick={async (e) => {
        e.stopPropagation();
        const ok = await copiar(texto);
        // Muda só o ícone (a linha não mexe) e diz o que aconteceu de verdade.
        setCopiou(ok);
        toast({ title: ok ? "Copiado" : "Não copiou", variant: ok ? "default" : "destructive" });
        if (ok) setTimeout(() => setCopiou(false), 1500);
      }}
    >
      {copiou ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

// No celular o /results passa da largura da tela e o navegador alarga a área de layout: preso a ela (inset), o modal saía
// cortado à direita. Preso à área VISÍVEL (visualViewport), cabe sempre, com 12 px de margem (16 a partir de 768 px).
function useCaixaVisivel() {
  const ler = () => {
    const vv = window.visualViewport;
    const w = vv?.width ?? window.innerWidth;
    const h = vv?.height ?? window.innerHeight;
    const m = w < 768 ? 12 : 16;
    return { left: (vv?.offsetLeft ?? 0) + m, top: (vv?.offsetTop ?? 0) + m, width: w - 2 * m, height: h - 2 * m };
  };
  const [caixa, setCaixa] = useState(ler);
  useEffect(() => {
    const vv = window.visualViewport;
    const atualiza = () => setCaixa(ler());
    vv?.addEventListener("resize", atualiza);
    vv?.addEventListener("scroll", atualiza);
    window.addEventListener("resize", atualiza);
    return () => {
      vv?.removeEventListener("resize", atualiza);
      vv?.removeEventListener("scroll", atualiza);
      window.removeEventListener("resize", atualiza);
    };
  }, []);
  return caixa;
}

export function VerAgenteModal({
  storeId,
  storeName,
  conversaInicial = null,
  onClose,
}: {
  storeId: string;
  storeName?: string | null;
  conversaInicial?: string | null;
  onClose: () => void;
}) {
  const [aberta, setAberta] = useState<string | null>(conversaInicial);
  const caixa = useCaixaVisivel();

  const lista = useQuery({
    queryKey: ["admin-robo-conversas", storeId],
    queryFn: () => customFetch<RespostaConversas>(`/api/admin/robo/conversas?storeId=${encodeURIComponent(storeId)}`),
    retry: false,
  });
  const loja = lista.data?.loja;
  const nomeDaLoja = (typeof loja === "string" ? loja : loja?.nome ?? loja?.name) || storeName || "";

  // Conversa do link que não está na lista (o robô nunca falou ali) entra por cima, avulsa: quem clicou quis essa.
  const conversas = useMemo(() => {
    const todas = lista.data?.conversas ?? [];
    if (aberta && !todas.some((c) => c.phone === aberta)) {
      return [{ phone: aberta, nome: null, ultima: null, ultimaQuem: null, ultimaTipo: null, ultimaEm: null, total: 0, pausa: null } as ConversaDoRobo, ...todas];
    }
    return todas;
  }, [lista.data, aberta]);
  const atual = conversas.find((c) => c.phone === aberta) ?? null;

  return (
    <DialogPrimitive.Root open onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <DialogPrimitive.Content
          className="fixed z-50 flex flex-col overflow-hidden rounded-xl border bg-background shadow-2xl focus:outline-none"
          style={caixa}
          aria-describedby={undefined}
          data-testid="ver-agente"
        >
          <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
            <DialogPrimitive.Title className="text-base font-semibold truncate">
              Agente{nomeDaLoja ? ` · ${nomeDaLoja}` : ""}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Fechar">
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>
          {lista.isError && (
            <div className="border-b bg-destructive/10 px-4 py-2 text-sm text-destructive" role="alert">
              {extractApiErrorMessage(lista.error, "Não foi possível carregar as conversas.")}
            </div>
          )}
          {/* Duas colunas que rolam sozinhas; no celular (< md), uma por vez. */}
          <div className="grid min-h-0 flex-1 md:grid-cols-[minmax(240px,320px)_1fr]">
            <div className={`min-h-0 overflow-y-auto border-r ${aberta ? "hidden md:block" : ""}`} data-testid="ver-agente-lista">
              {lista.isLoading ? (
                <p className="p-4 text-sm text-muted-foreground">Carregando…</p>
              ) : conversas.length === 0 ? (
                <div className="p-6 text-center" data-testid="ver-agente-vazio">
                  <p className="text-sm font-medium">Nenhuma conversa</p>
                  <p className="mt-1 text-xs text-muted-foreground">Assim que o robô responder alguém, a conversa aparece aqui.</p>
                </div>
              ) : (
                conversas.map((c) => (
                  <ItemDaLista key={c.phone} c={c} ativa={c.phone === aberta} onClick={() => setAberta(c.phone)} />
                ))
              )}
            </div>
            <div className={`min-h-0 flex-col ${aberta ? "flex" : "hidden md:flex"}`}>
              {atual ? (
                <Conversa key={atual.phone} storeId={storeId} conversa={atual} onVoltar={() => setAberta(null)} />
              ) : (
                <div className="m-auto p-6 text-center" data-testid="ver-agente-escolha">
                  <p className="text-sm font-medium">Escolha uma conversa</p>
                  <p className="mt-1 text-xs text-muted-foreground">A conversa escolhida aparece aqui, do jeito que o cliente viu.</p>
                </div>
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function ItemDaLista({ c, ativa, onClick }: { c: ConversaDoRobo; ativa: boolean; onClick: () => void }) {
  const midia = rotuloDaMidia(c.ultimaTipo);
  const corpo = midia ?? c.ultima ?? "";
  const previa =
    c.ultimaQuem === "robo" ? `Robô: ${corpo}` : c.ultimaAutomatica ? `Automática do WhatsApp: ${corpo}` : c.ultimaQuem === "loja" ? `Loja: ${corpo}` : corpo;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 border-b px-3 py-2.5 text-left hover:bg-muted/60 ${ativa ? "bg-muted" : ""}`}
      data-testid={`ver-agente-conversa-${c.phone}`}
    >
      <Avatar nome={c.nome} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{c.nome || telefoneDe(c)}</span>
          <span className="shrink-0 text-[11px] text-muted-foreground">{horaDaLista(c.ultimaEm)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{previa}</span>
          {c.pausa && (
            <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" data-testid="ver-agente-pausado">
              pausado
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function Conversa({ storeId, conversa: c, onVoltar }: { storeId: string; conversa: ConversaDoRobo; onVoltar: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const rolagem = useRef<HTMLDivElement>(null);
  const jaRolou = useRef(false);

  // A chave da consulta é a conversa: resposta atrasada de outra conversa cai na chave dela e não aparece aqui (14.14).
  const mensagens = useQuery({
    queryKey: ["admin-robo-mensagens", storeId, c.phone],
    queryFn: async () => {
      const r = await customFetch<MensagemDoRobo[] | { mensagens: MensagemDoRobo[] }>(
        `/api/admin/robo/mensagens?storeId=${encodeURIComponent(storeId)}&phone=${encodeURIComponent(c.phone)}&limite=300`,
      );
      return Array.isArray(r) ? r : r.mensagens ?? [];
    },
    retry: false,
  });
  // Estado lido ao vivo; sem estado, sem interruptor (melhor ausente que mentindo).
  const chaveEstado = ["admin-robo-agente", storeId, c.phone];
  const estado = useQuery({
    queryKey: chaveEstado,
    queryFn: () =>
      customFetch<EstadoDoAgente>("/api/admin/robo/agente", {
        method: "POST",
        body: JSON.stringify({ storeId, phone: c.phone, acao: "estado" }),
      }),
    retry: false,
    staleTime: 0,
    enabled: INTERRUPTOR_PRONTO,
  });

  // Desce ao fim só ao abrir; dado que chega depois não puxa quem subiu para ler (armadilha 13).
  useEffect(() => {
    if (jaRolou.current || !mensagens.data || !rolagem.current) return;
    rolagem.current.scrollTop = rolagem.current.scrollHeight;
    jaRolou.current = true;
  }, [mensagens.data]);

  const muda = async (ligar: boolean) => {
    const antes = qc.getQueryData<EstadoDoAgente>(chaveEstado);
    qc.setQueryData<EstadoDoAgente>(chaveEstado, { pausado: !ligar, motivo: ligar ? null : "admin" });
    try {
      const r = await customFetch<EstadoDoAgente>("/api/admin/robo/agente", {
        method: "POST",
        body: JSON.stringify({ storeId, phone: c.phone, acao: ligar ? "retomar" : "pausar" }),
      });
      qc.setQueryData<EstadoDoAgente>(chaveEstado, r);
      qc.invalidateQueries({ queryKey: ["admin-robo-conversas", storeId] });
    } catch (err) {
      qc.setQueryData<EstadoDoAgente>(chaveEstado, antes);
      toast({ title: extractApiErrorMessage(err, "Não foi possível mudar o robô nesta conversa."), variant: "destructive" });
    }
  };

  const lista = mensagens.data ?? [];
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-3 py-2.5">
        <button type="button" onClick={onVoltar} className="md:hidden -ml-1 rounded p-1 text-muted-foreground hover:text-foreground" aria-label="Conversas" data-testid="ver-agente-voltar">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <Avatar nome={c.nome} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold" data-testid="ver-agente-titulo">{c.nome || telefoneDe(c)}</div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            {c.nome && <span>{telefoneDe(c)}</span>}
            <BotaoCopiar texto={`${storeId}:${c.phone}`} rotulo="Copiar o ID da conversa" />
          </div>
        </div>
        {estado.data && (
          <label className="flex shrink-0 items-center gap-2 text-sm" data-testid="ver-agente-robo">
            <Switch checked={!estado.data.pausado} onCheckedChange={(v) => muda(v)} aria-label="Robô nesta conversa" />
            Robô
          </label>
        )}
      </div>
      {mensagens.isError && (
        <div className="border-b bg-destructive/10 px-4 py-2 text-sm text-destructive" role="alert">
          {extractApiErrorMessage(mensagens.error, "Não foi possível carregar as mensagens desta conversa.")}
        </div>
      )}
      <div ref={rolagem} className="min-h-0 flex-1 overflow-y-auto bg-muted/30 px-3 py-3" data-testid="ver-agente-mensagens">
        {mensagens.isLoading && <p className="text-center text-sm text-muted-foreground">Carregando…</p>}
        {lista.map((m, i) => {
          const novoDia = i === 0 || dia(lista[i - 1]!.em) !== dia(m.em);
          return (
            <div key={`${m.id}`}>
              {novoDia && (
                <div className="my-3 text-center">
                  <span className="rounded-md bg-background px-2 py-0.5 text-[11px] text-muted-foreground shadow-sm">{dia(m.em)}</span>
                </div>
              )}
              <Balao m={m} storeId={storeId} phone={c.phone} />
            </div>
          );
        })}
      </div>
    </>
  );
}

function Balao({ m, storeId, phone }: { m: MensagemDoRobo; storeId: string; phone: string }) {
  // msg = o id da UAZAPI ou, sem ele (robô e sistema), o id da linha: com loja, acha a linha por SQL.
  const idCopiavel = [`msg=${m.messageId ?? m.id}`, `loja=${storeId}`, `tel=${phone}`, `em=${m.em}`, m.execucao ? `exec=${m.execucao}` : null]
    .filter(Boolean)
    .join(" ");
  if (m.quem === "sistema") {
    return (
      <div className="group my-2 flex items-center justify-center gap-1 text-center text-xs italic text-muted-foreground" data-testid="ver-agente-sistema">
        <span>{m.texto}</span>
        <span className="text-[10px] not-italic">{hora(m.em)}</span>
        <BotaoCopiar texto={idCopiavel} rotulo="Copiar o ID da mensagem" className="opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100" />
      </div>
    );
  }
  const direita = m.quem !== "cliente";
  const rotulo = (m.origem && ROTULO_DA_ORIGEM[m.origem]) || (m.automatica ? "Automática do WhatsApp" : m.quem === "loja" ? "Loja" : null);
  const midia = rotuloDaMidia(m.tipo);
  return (
    <div className={`group my-1 flex ${direita ? "justify-end" : "justify-start"}`} data-testid={`ver-agente-msg-${m.quem}${m.automatica ? "-automatica" : ""}`}>
      <div
        className={`max-w-[85%] md:max-w-[70%] rounded-lg px-3 py-1.5 text-sm shadow-sm ${
          direita ? "rounded-tr-sm bg-emerald-50 dark:bg-emerald-950/40" : "rounded-tl-sm bg-background"
        }`}
      >
        {rotulo && <div className="mb-0.5 text-[11px] font-semibold text-muted-foreground">{rotulo}</div>}
        {midia ? (
          <>
            <div>{m.tipo === "audio" ? "🎤 Áudio" : midia}</div>
            {(m.transcricao || m.texto) && <div className="mt-0.5 italic text-muted-foreground whitespace-pre-wrap">{m.transcricao || m.texto}</div>}
          </>
        ) : (
          <div className="whitespace-pre-wrap break-words">{m.texto}</div>
        )}
        <div className="mt-0.5 flex items-center justify-end gap-1 text-[10px] text-muted-foreground">
          <BotaoCopiar texto={idCopiavel} rotulo="Copiar o ID da mensagem" className="opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100" />
          {m.quem === "robo" && <Bot className="h-3 w-3" aria-label="Robô" />}
          <span>{hora(m.em)}</span>
        </div>
      </div>
    </div>
  );
}
